import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT, CONTACT_CATEGORIES } from "@/lib/content";
import { fadeUp, stagger } from "@/lib/motion";
import { Mail, Phone, Instagram, Linkedin, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: CONTACT_CATEGORIES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error("Please fill your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thanks — your note has been sent to Parin.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", category: CONTACT_CATEGORIES[0], message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Could not send. Please try WhatsApp or email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-ink-950 py-28 md:py-40 border-t border-ivory/[0.06]"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="grid grid-cols-12 gap-6 md:gap-12"
        >
          {/* Left */}
          <motion.div variants={fadeUp} className="col-span-12 md:col-span-5">
            <div className="font-mono-caps text-[10px] text-copper mb-6">
              <span className="mr-3">§ 09</span> Contact
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[0.98] text-ivory tracking-tight">
              Let&apos;s Build,<br />
              <span className="italic text-copper">Collaborate</span><br />
              or Invest Together.
            </h2>

            <div className="mt-12 space-y-6 text-ivory">
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 border-b border-ivory/[0.08] pb-4 hover:text-copper transition-colors"
              >
                <Mail size={16} className="text-copper" />
                <span className="font-serif italic text-xl md:text-2xl">{CONTACT.email}</span>
              </a>
              <a
                href={`tel:${CONTACT.phone_e164}`}
                data-testid="contact-phone-link"
                className="group flex items-center gap-4 border-b border-ivory/[0.08] pb-4 hover:text-copper transition-colors"
              >
                <Phone size={16} className="text-copper" />
                <span className="font-serif italic text-xl md:text-2xl">{CONTACT.phone}</span>
              </a>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`https://wa.me/${CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="inline-flex items-center gap-2 font-mono-caps text-[10px] border border-ivory/25 hover:border-copper hover:text-copper text-ivory px-4 py-2.5 transition-colors"
                >
                  <MessageCircle size={12} /> WhatsApp
                </a>
                <a
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="inline-flex items-center gap-2 font-mono-caps text-[10px] border border-ivory/25 hover:border-copper hover:text-copper text-ivory px-4 py-2.5 transition-colors"
                >
                  <Instagram size={12} /> Instagram
                </a>
                <a
                  href={CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  className="inline-flex items-center gap-2 font-mono-caps text-[10px] border border-ivory/25 hover:border-copper hover:text-copper text-ivory px-4 py-2.5 transition-colors"
                >
                  <Linkedin size={12} /> LinkedIn
                </a>
              </div>
            </div>

            <div className="mt-12">
              <div className="font-mono-caps text-[9px] text-ivory/50 mb-3">What can I help with</div>
              <div className="flex flex-wrap gap-2">
                {CONTACT_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm((f) => ({ ...f, category: c }))}
                    data-testid={`category-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-[10px] font-mono-caps px-3 py-1.5 border transition-colors duration-300 ${
                      form.category === c
                        ? "border-copper text-copper bg-copper/10"
                        : "border-ivory/15 text-ivory/70 hover:border-ivory/40"
                    }`}
                    type="button"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div variants={fadeUp} className="col-span-12 md:col-span-7">
            <form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="border border-ivory/[0.08] bg-ink-900/40 p-8 md:p-12"
            >
              <div className="font-mono-caps text-[10px] text-copper mb-8">Enquiry Form</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                <Field label="Name" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={upd("name")}
                    data-testid="input-name"
                    placeholder="Your full name"
                    className="w-full bg-transparent border-0 border-b border-ivory/[0.12] focus:border-copper py-3 text-ivory placeholder-ivory/30 font-light outline-none transition-colors"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={upd("email")}
                    data-testid="input-email"
                    placeholder="you@company.com"
                    className="w-full bg-transparent border-0 border-b border-ivory/[0.12] focus:border-copper py-3 text-ivory placeholder-ivory/30 font-light outline-none transition-colors"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={upd("phone")}
                    data-testid="input-phone"
                    placeholder="+91 —"
                    className="w-full bg-transparent border-0 border-b border-ivory/[0.12] focus:border-copper py-3 text-ivory placeholder-ivory/30 font-light outline-none transition-colors"
                  />
                </Field>
                <Field label="Category">
                  <select
                    value={form.category}
                    onChange={upd("category")}
                    data-testid="input-category"
                    className="w-full bg-transparent border-0 border-b border-ivory/[0.12] focus:border-copper py-3 text-ivory font-light outline-none transition-colors appearance-none"
                  >
                    {CONTACT_CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-ink-950 text-ivory">
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-10">
                <Field label="Message" required>
                  <textarea
                    value={form.message}
                    onChange={upd("message")}
                    data-testid="input-message"
                    placeholder="Tell me a little about what you're planning…"
                    rows={5}
                    className="w-full bg-transparent border-0 border-b border-ivory/[0.12] focus:border-copper py-3 text-ivory placeholder-ivory/30 font-light outline-none transition-colors resize-none"
                  />
                </Field>
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-between gap-6">
                <p className="text-[11px] text-ivory/40 max-w-xs leading-relaxed">
                  Replies typically within 24 hours. All enquiries are confidential.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-enquiry"
                  className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-hover disabled:opacity-60 disabled:cursor-not-allowed text-ink-950 px-6 py-4 font-mono-caps text-[10px] transition-colors"
                >
                  {submitting ? "Sending…" : done ? "Sent · Send another" : "Send Enquiry"}
                  <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono-caps text-[9px] text-ivory/50 flex items-center gap-2">
        {label}
        {required && <span className="text-copper">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
